// Phase 1A Step 35 — SIKA plan card.
// The "generated plan" panel that slides into the SIKA conversation card.
// Extracted from SikaConversation.tsx to keep both files under 250 LOC.

import { fmtBig } from "./calc-utils.ts";
import type { SikaPlan } from "./sika-data.ts";

interface SikaPlanCardProps {
  plan: SikaPlan;
}

export function SikaPlanCard({ plan }: SikaPlanCardProps): JSX.Element {
  return (
    <div
      className="sz-l-sika-plan"
      style={{
        marginTop: 8,
        background: "linear-gradient(180deg, #1A1408 0%, #0E0E0E 100%)",
        border: "1px solid var(--accent-deep)",
        padding: "20px 22px 22px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 14,
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: 9,
            color: "var(--accent)",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          · Plan generated
        </span>
        <span
          className="mono"
          style={{
            fontSize: 9,
            color: "var(--text-dim)",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
          }}
        >
          {plan.tier}
        </span>
      </div>

      <div
        className="serif"
        style={{
          fontSize: 18,
          color: "var(--text)",
          fontStyle: "italic",
          marginBottom: 14,
          lineHeight: 1.25,
        }}
      >
        {plan.goal}
      </div>

      {/* Plan stats row */}
      <div
        className="sz-l-sika-plan-stats"
        style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr 1fr 1fr",
          gap: 0,
          borderLeft: "1px solid var(--border)",
        }}
      >
        <PlanStat label="Target" value={`GH₵${fmtBig(plan.target)}`} accent />
        <PlanStat label="Opening" value={`GH₵${plan.stake}`} />
        <PlanStat label="Odds" value={`${plan.odds.toFixed(1)}×`} />
        <PlanStat label="Weeks" value={`${plan.weeks}`} />
      </div>

      {plan.chains && (
        <div
          style={{
            marginTop: 14,
            paddingTop: 12,
            borderTop: "1px solid var(--border)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div
            className="mono"
            style={{
              fontSize: 9,
              color: "var(--text-dim)",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            Three parallel chains
          </div>
          {plan.chains.map((c) => (
            <div
              key={c.market}
              className="mono"
              style={{ fontSize: 11, color: "var(--text-muted)", letterSpacing: "0.04em" }}
            >
              {c.market} · GH₵{c.stake} @ {c.odds.toFixed(1)}× × {c.weeks}w
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface PlanStatProps {
  label: string;
  value: string;
  accent?: boolean;
}

function PlanStat({ label, value, accent }: PlanStatProps): JSX.Element {
  return (
    <div
      style={{
        padding: "10px 12px",
        borderRight: "1px solid var(--border)",
        borderTop: "1px solid var(--border)",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <div
        className="mono"
        style={{
          fontSize: 9,
          color: "var(--text-dim)",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        className="display"
        style={{
          fontSize: accent ? 22 : 18,
          color: accent ? "var(--accent)" : "var(--text)",
          letterSpacing: "0.01em",
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}
